import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, User, Coins, FileText, Download, CheckCircle2, XCircle, Clock, Gift, RefreshCw, AlertTriangle } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { adminApi } from "../../api/auth";

const MET = { paypal: "PayPal", mp_ar: "MercadoPago AR", mp_cl: "MercadoPago CL",
  khipu: "Khipu CL", khipu_cl: "Khipu CL", khipu_ar: "Khipu AR", khipu_pe: "Khipu PE",
  crypto: "USDT", manual: "Manual" };
const fmtDate = (s) => s ? new Date(s.replace(' ', 'T')).toLocaleString("es-AR") : "—";
const fmtAmount = (a, c) => `${Number(a).toLocaleString("es-AR")} ${c}`;

const StatusBadge = ({ status }) => {
  const colors = {
    approved: "bg-green-950/40 text-green-400 border-green-900/60",
    pending:  "bg-amber-950/40 text-amber-400 border-amber-900/60",
    rejected: "bg-red-950/40 text-red-400 border-red-900/60",
    cancelled:"bg-white/5 text-[#b8b3a8] border-[#3a3328]",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded ${colors[status] || colors.cancelled}`}>
      {status}
    </span>
  );
};

export default function AdminPlayer() {
  const { account } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminApi.player(account).then(setData).catch((e) => setErr(e.message));
  }, [account]);

  // Build claims lookup
  const claimsByTxn = {};
  if (data?.claims) data.claims.forEach((c) => { claimsByTxn[c.txn_id] = c; });

  const downloadPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    let y = 40;

    // Header
    doc.setFontSize(18);
    doc.setTextColor(212, 168, 67);
    doc.text("L2 Aetheris - Auditoria de Donaciones", 40, y); y += 22;
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Reporte generado: ${new Date().toLocaleString("es-AR")}`, 40, y); y += 16;

    // Account info
    doc.setFontSize(13);
    doc.setTextColor(0);
    doc.text("Cuenta", 40, y); y += 4;
    doc.setDrawColor(212, 168, 67); doc.line(40, y, W - 40, y); y += 16;
    doc.setFontSize(10);
    const a = data.account;
    [
      ["Login", a.login],
      ["Access Level", String(a.access_level)],
      ["Last IP", a.last_ip || "—"],
      ["Personajes activos", String(data.characters.length)],
    ].forEach(([k, v]) => { doc.text(`${k}:`, 40, y); doc.text(String(v), 160, y); y += 14; });

    // Resumen
    y += 8;
    doc.setFontSize(13);
    doc.text("Resumen de donaciones", 40, y); y += 4;
    doc.line(40, y, W - 40, y); y += 6;
    const s = data.summary;
    const byCur = Object.entries(s.amount_by_currency || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${k}: ${Number(v).toLocaleString("es-AR")}`).join("  ·  ") || "—";
    autoTable(doc, {
      startY: y,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 3 },
      body: [
        ["Total ordenes", String(s.orders_total)],
        ["Aprobadas", String(s.orders_approved)],
        ["Monto aprobado", byCur],
        ["Coins acreditadas", Number(s.coins_approved).toLocaleString("es-AR")],
        ["Coins entregadas (claim hecho)", Number(s.coins_delivered).toLocaleString("es-AR")],
        ["Coins pendientes de claim", Number(s.coins_pending).toLocaleString("es-AR")],
      ],
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 200 } },
      margin: { left: 40, right: 40 },
    });
    y = doc.lastAutoTable.finalY + 12;

    // Personajes
    if (data.characters.length > 0) {
      doc.setFontSize(13); doc.text("Personajes", 40, y); y += 4;
      doc.line(40, y, W - 40, y); y += 6;
      autoTable(doc, {
        startY: y,
        head: [["Nombre", "Clase", "Lvl", "Online (h)", "PvP", "PK"]],
        body: data.characters.map((c) => [c.name, c.class, c.level, c.online_h, c.pvp, c.pk]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [40, 30, 20], textColor: [212, 168, 67] },
        margin: { left: 40, right: 40 },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // Ordenes
    doc.setFontSize(13); doc.text("Historial de donaciones", 40, y); y += 4;
    doc.line(40, y, W - 40, y); y += 6;
    autoTable(doc, {
      startY: y,
      head: [["Fecha", "Order UID", "Metodo", "Monto", "Coins", "Status", "Claim", "IP", "Pais"]],
      body: data.orders.map((o) => {
        const cl = claimsByTxn[o.txn_id];
        const claimStr = !cl ? "—" : (cl.claimed ? `OK ${cl.claimed_character || ""}` : "Pendiente");
        return [
          fmtDate(o.created_at),
          o.order_uid,
          MET[o.method] || o.method,
          fmtAmount(o.amount, o.currency),
          Number(o.coins_total).toLocaleString("es-AR"),
          o.status,
          claimStr,
          o.ip || "—",
          o.country || "—",
        ];
      }),
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [40, 30, 20], textColor: [212, 168, 67] },
      margin: { left: 40, right: 40 },
      didDrawPage: (d) => {
        doc.setFontSize(8); doc.setTextColor(150);
        doc.text(`Pagina ${doc.internal.getNumberOfPages()}`, W - 60, doc.internal.pageSize.getHeight() - 20);
      },
    });
    y = doc.lastAutoTable.finalY + 12;

    // Detalle por orden con user agent
    if (data.orders.length > 0) {
      doc.addPage();
      y = 40;
      doc.setFontSize(13); doc.setTextColor(0);
      doc.text("Detalle expandido por orden", 40, y); y += 4;
      doc.setDrawColor(212, 168, 67); doc.line(40, y, W - 40, y); y += 12;

      data.orders.slice(0, 30).forEach((o, idx) => {
        if (y > 720) { doc.addPage(); y = 40; }
        const cl = claimsByTxn[o.txn_id];
        doc.setFontSize(10); doc.setTextColor(212, 168, 67);
        doc.text(`#${idx + 1}  ${o.order_uid}  -  ${o.status.toUpperCase()}`, 40, y); y += 14;
        doc.setFontSize(8); doc.setTextColor(60);
        const lines = [
          `Fecha: ${fmtDate(o.created_at)} | Aprobada: ${fmtDate(o.approved_at)} | Claim: ${fmtDate(o.claimed_at)}`,
          `Metodo: ${MET[o.method] || o.method} | Monto: ${fmtAmount(o.amount, o.currency)} | Coins: ${o.coins_total} (base ${o.coins_base} + bonus ${o.coins_bonus} = ${o.bonus_percent}%)`,
          `Email: ${o.email} | TXN: ${o.txn_id || "—"}`,
          `IP: ${o.ip || "—"} | Pais: ${o.country || "—"} | Region: ${o.region || "—"} | Ciudad: ${o.city || "—"}`,
          `User-Agent: ${(o.user_agent || "—").substring(0, 130)}`,
          `Gateway status: ${o.gateway_status || "—"}`,
          cl ? `Claim: ${cl.claimed ? "ENTREGADO" : "PENDIENTE"} | Character: ${cl.claimed_character || "—"} | Account claim: ${cl.claimed_account || "—"} | HWID: ${cl.claimed_hwid || "—"} | IP claim: ${cl.claimed_ip || "—"} | Hora: ${fmtDate(cl.claimed_time)}`
            : `Claim: NO HAY REGISTRO en tabla claims`,
        ];
        lines.forEach((l) => { doc.text(l, 40, y, { maxWidth: W - 80 }); y += 11; });
        y += 8;
      });
    }

    doc.save(`l2aetheris_audit_${a.login}_${Date.now()}.pdf`);
  };

  if (err) return <div className="text-red-400 p-4 border border-red-900/40 rounded">{err}</div>;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-[#d4a843]" />;

  const { account: acc, characters, orders, summary, audit } = data;

  return (
    <div className="space-y-5" data-testid="admin-player">
      <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-xs text-[#b8b3a8] hover:text-[#d4a843]">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="ornament"><span>{acc.login}</span></div>
          <h1 className="section-title mt-3 text-3xl flex items-center gap-3">
            <User className="w-7 h-7 text-[#d4a843]" /> {acc.login}
            {acc.access_level >= 100 && (
              <span className="text-xs uppercase bg-purple-900/40 text-purple-300 px-2 py-1 rounded">GM lvl {acc.access_level}</span>
            )}
          </h1>
          <p className="text-sm text-[#b8b3a8] mt-1">Last IP: {acc.last_ip || "—"}</p>
        </div>
        <button onClick={downloadPDF}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4a843] text-black text-sm font-medium rounded hover:bg-[#e0b860]"
          data-testid="export-pdf">
          <FileText className="w-4 h-4" /> Descargar PDF auditoria
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded p-3">
          <div className="text-[10px] text-[#7e7768] uppercase tracking-wider">Ordenes</div>
          <div className="text-2xl text-white font-semibold">{summary.orders_total}</div>
          <div className="text-xs text-green-400">{summary.orders_approved} aprobadas</div>
        </div>
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded p-3">
          <div className="text-[10px] text-[#7e7768] uppercase tracking-wider">Coins acreditadas</div>
          <div className="text-2xl text-amber-300 font-semibold flex items-center gap-1.5">
            <Coins className="w-5 h-5" /> {summary.coins_approved.toLocaleString()}
          </div>
        </div>
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded p-3">
          <div className="text-[10px] text-[#7e7768] uppercase tracking-wider">Coins entregadas</div>
          <div className="text-2xl text-green-400 font-semibold">{summary.coins_delivered.toLocaleString()}</div>
        </div>
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded p-3">
          <div className="text-[10px] text-[#7e7768] uppercase tracking-wider">Coins pendientes</div>
          <div className="text-2xl text-amber-400 font-semibold">{summary.coins_pending.toLocaleString()}</div>
        </div>
      </div>

      {/* Por moneda */}
      {Object.entries(summary.amount_by_currency || {}).some(([, v]) => v > 0) && (
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded p-4">
          <h3 className="text-xs uppercase tracking-wider text-[#d4a843] mb-2">Monto total aprobado</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(summary.amount_by_currency).filter(([, v]) => v > 0).map(([cur, val]) => (
              <div key={cur} className="text-white">
                <span className="text-2xl font-semibold">{Number(val).toLocaleString("es-AR")}</span>
                <span className="text-sm text-[#7e7768] ml-1">{cur}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Personajes */}
      {characters.length > 0 && (
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded overflow-hidden">
          <h3 className="text-xs uppercase tracking-wider text-[#d4a843] p-3 border-b border-[#3a3328]">Personajes ({characters.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase text-[#7e7768] border-b border-[#3a3328]">
                <th className="text-left px-3 py-2">Nombre</th><th className="text-left">Clase</th>
                <th className="text-right">Lvl</th><th className="text-right">Online (h)</th>
                <th className="text-right">PvP</th><th className="text-right">PK</th><th>Status</th>
              </tr></thead>
              <tbody>{characters.map((c) => (
                <tr key={c.name} className="border-b border-[#3a3328]/40">
                  <td className="px-3 py-2 text-white">{c.name}</td>
                  <td className="text-[#b8b3a8]">{c.class}</td>
                  <td className="text-right text-[#b8b3a8]">{c.level}</td>
                  <td className="text-right text-[#b8b3a8]">{c.online_h}</td>
                  <td className="text-right text-red-300">{c.pvp}</td>
                  <td className="text-right text-red-400">{c.pk}</td>
                  <td className="text-center">
                    {c.online ? <span className="text-green-400 text-xs">● online</span> : <span className="text-[#7e7768] text-xs">offline</span>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ordenes */}
      <div className="border border-[#3a3328] bg-[#15110a]/70 rounded overflow-hidden">
        <h3 className="text-xs uppercase tracking-wider text-[#d4a843] p-3 border-b border-[#3a3328]">
          Historial de donaciones ({orders.length})
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase text-[#7e7768] border-b border-[#3a3328]">
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left">Order</th>
              <th className="text-left">Metodo</th>
              <th className="text-right">Monto</th>
              <th className="text-right">Coins</th>
              <th>Status</th>
              <th className="text-center">Claim</th>
              <th className="text-left">IP / GEO</th>
            </tr></thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={8} className="text-center text-[#7e7768] py-4">Sin donaciones registradas</td></tr>
              )}
              {orders.map((o) => {
                const cl = claimsByTxn[o.txn_id];
                return (
                <tr key={o.order_uid} className="border-b border-[#3a3328]/40">
                  <td className="px-3 py-2 text-xs text-[#b8b3a8] whitespace-nowrap">{fmtDate(o.created_at)}</td>
                  <td className="font-mono text-xs text-[#b8b3a8]">{o.order_uid}</td>
                  <td className="text-[#b8b3a8]">{MET[o.method] || o.method}</td>
                  <td className="text-right text-white whitespace-nowrap">{fmtAmount(o.amount, o.currency)}</td>
                  <td className="text-right text-amber-300">{Number(o.coins_total).toLocaleString()}</td>
                  <td className="text-center"><StatusBadge status={o.status} /></td>
                  <td className="text-center text-xs">
                    {!cl && <span className="text-[#7e7768]">—</span>}
                    {cl && cl.claimed === 1 && <span className="inline-flex items-center gap-1 text-green-400"><CheckCircle2 className="w-3 h-3" /> {cl.claimed_character || "OK"}</span>}
                    {cl && cl.claimed === 0 && <span className="inline-flex items-center gap-1 text-amber-400"><Clock className="w-3 h-3" /> pendiente</span>}
                  </td>
                  <td className="text-xs text-[#b8b3a8] whitespace-nowrap">
                    {o.ip} {o.country && <span className="text-[#7e7768]">({o.country}{o.city ? "/" + o.city : ""})</span>}
                  </td>
                  <td className="text-right pr-2">
                    {o.status === "approved" && (
                      <RefundButton orderUid={o.order_uid} onDone={() => window.location.reload()} />
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit log */}
      {audit?.length > 0 && (
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded overflow-hidden">
          <h3 className="text-xs uppercase tracking-wider text-[#d4a843] p-3 border-b border-[#3a3328]">
            Audit log ({audit.length} eventos)
          </h3>
          <div className="max-h-96 overflow-y-auto text-xs">
            {audit.map((e) => (
              <div key={e.id} className="px-3 py-1.5 border-b border-[#3a3328]/30 flex gap-3">
                <span className="text-[#7e7768] whitespace-nowrap">{fmtDate(e.created_at)}</span>
                <span className="text-[#d4a843] font-mono whitespace-nowrap">{e.event}</span>
                <span className="text-[#b8b3a8] truncate flex-1" title={e.details}>{e.details}</span>
                <span className="text-[#7e7768] whitespace-nowrap">{e.ip}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RefundButton({ orderUid, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!confirm(`¿Reembolsar la orden ${orderUid}? Esta accion no se puede deshacer.`)) return;
    setLoading(true); setErr("");
    try {
      const res = await adminApi.refund(orderUid, reason);
      alert(`Reembolso procesado. ${res.refunded_at_gateway ? "Confirmado en el gateway." : "Marcado en DB. El gateway requiere accion manual (cripto)."}`);
      onDone && onDone();
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Reembolsar"
        className="text-amber-400 hover:text-amber-300 p-1" data-testid={`refund-${orderUid}`}>
        <RefreshCw className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <div className="absolute right-3 z-10 mt-1 w-72 p-3 bg-[#1a1610] border border-amber-900/40 rounded shadow-xl text-left">
      <div className="flex items-center gap-2 text-xs text-amber-400 mb-2">
        <AlertTriangle className="w-3.5 h-3.5" /> Reembolsar
      </div>
      <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Razon del refund (opcional)"
        className="w-full bg-[#0a0a0d] border border-[#3a3328] text-white text-xs rounded p-2 mb-2" />
      {err && <div className="text-xs text-red-400 mb-2">{err}</div>}
      <div className="flex gap-2">
        <button onClick={submit} disabled={loading}
          className="flex-1 px-3 py-1 text-xs bg-amber-600 text-black rounded hover:bg-amber-500 disabled:opacity-50">
          {loading ? "..." : "Confirmar"}
        </button>
        <button onClick={() => { setOpen(false); setErr(""); }}
          className="px-3 py-1 text-xs border border-[#3a3328] text-[#b8b3a8] rounded hover:bg-white/5">
          Cancelar
        </button>
      </div>
    </div>
  );
}
